/* RESPONSIBLE TEAM: team-frontend-tech */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
/* eslint-disable ember/require-computed-property-dependencies */
/* eslint-disable ember/no-classic-classes */
import { isPresent, isNone } from '@ember/utils';
import { copy } from 'ember-copy';
import { A } from '@ember/array';
import EmberObject, { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import moment from 'moment-timezone';
import { task, timeout } from 'ember-concurrency';
import { STATUS_CODES, ERROR_CODES, generateCacheKey } from './cache-helpers';

const QUOTA_EXCEEDED_EXCEPTION_MESSAGE_REGEX = /quota|storage/;

export default EmberObject.extend({
  cache: undefined,
  modelConfig: undefined,
  cacheConfig: readOnly('cache.cacheConfiguration'),
  modelName: undefined,
  errors: undefined,
  id: undefined,

  init() {
    this._super(...arguments);
    this._assertPresentOrThrow(
      `cache`,
      `Attempted to create a cache item without reference to the cache.`,
    );
    this._assertPresentOrThrow(
      `modelConfig`,
      `Attempted to create a cache item without a model configuration.`,
    );
    this._assertPresentOrThrow(
      `cacheConfig`,
      `Attempted to create a cache item without a cache configuration.`,
    );
    this._assertPresentOrThrow(
      `modelName`,
      `Attempted to create a cache item without a model name.`,
    );
    this.set('errors', A());
  },

  fetch: task(function* () {
    this.errors.clear();
    let status = this.status();
    let data;
    switch (this.status()) {
      case STATUS_CODES.VALID: {
        this.log(`✅ Valid data found in cache`);
        data = yield this.handleValidLocalData();
        break;
      }
      case STATUS_CODES.NOTPRESENT: {
        this.log(`❌ No data found in cache`);
        data = yield this.handleMissingLocalData();
        break;
      }
      case STATUS_CODES.EXPIRED: {
        this.log(`⚠️ Expired data found in cache`);
        data = yield this.handleExpiredOrStaleLocalData();
        break;
      }
      case STATUS_CODES.STALE: {
        this.log(`⚠️ Stale data found in cache`);
        data = yield this.handleExpiredOrStaleLocalData();
        break;
      }
    }
    return {
      status,
      data,
      errors: this.errors.map((error) => copy(error)),
    };
  }),

  key: computed(`cacheConfig.namespace`, `modelName`, `id`, function () {
    return generateCacheKey(this.get('cacheConfig.namespace'), this.modelName, this.id);
  }),

  expiryTimestamp: computed(`cacheConfig.expiryTime`, `cacheConfig.expiryPeriod`, function () {
    return moment()
      .add(this.get('cacheConfig.expiryTime'), this.get('cacheConfig.expiryPeriod'))
      .unix();
  }),

  status() {
    if (!this.hasData()) {
      return STATUS_CODES.NOTPRESENT;
    } else if (this.dataHasExpired()) {
      return STATUS_CODES.EXPIRED;
    } else if (this.dataIsStale()) {
      return STATUS_CODES.STALE;
    } else {
      return STATUS_CODES.VALID;
    }
  },

  dataHasExpired() {
    return moment().unix() > this.cacheObject().expires;
  },

  dataIsStale() {
    return this.cacheObject().fingerprint !== this.get('modelConfig.fingerprint');
  },

  hasData() {
    if (Ember.testing && !window.__testing_ember_model_data_cache__) {
      this.log(
        `⏩ Skipping local storage in test mode. Set 'window.__testing_ember_model_data_cache__' to true to override this`,
      );
      return false;
    } else {
      return isPresent(this._getItemSafely(this.key));
    }
  },

  getData() {
    if (this.hasData()) {
      return this.cacheObject().data;
    }
  },

  setData(data) {
    if (Ember.testing && !window.__testing_ember_model_data_cache__) {
      this.log(
        `⏩ Skipping local storage in test mode. Set 'window.__testing_ember_model_data_cache__' to true to override this`,
      );
    } else {
      try {
        this.log(`✍️ Setting local data`);
        localStorage.setItem(
          this.key,
          JSON.stringify({
            data,
            fingerprint: this.get('modelConfig.fingerprint'),
            expires: this.expiryTimestamp,
          }),
        );
      } catch (error) {
        this._handleSetDataError(error);
      }
    }
  },

  removeData() {
    this.log(`🗑 Removing local data`);
    localStorage.removeItem(this.key);
  },

  cacheObject() {
    return JSON.parse(this._getItemSafely(this.key));
  },

  handleValidLocalData() {
    let refreshTaskInstance = this.queueRemoteRefresh.perform();
    this.cache._addRefreshTask(this.key, refreshTaskInstance);
    return this.loadFromLocalCache.perform();
  },

  handleMissingLocalData() {
    return this.fetchFromRemote.perform();
  },

  handleExpiredOrStaleLocalData() {
    this.removeData();
    return this.fetchFromRemote.perform();
  },

  loadFromLocalCache: task(function* () {
    this.log(`💾 Loading from local cache`);
    return yield this.modelConfig.load(this.getData());
  }),

  fetchFromRemote: task(function* () {
    this.log(`🚚 Fetching from remote`);
    let remoteResponse = yield this.modelConfig.fetch(this.id);
    let model = yield this.modelConfig.load(remoteResponse);
    this.setData(remoteResponse);
    return model;
  }),

  queueRemoteRefresh: task(function* () {
    let refreshDelay = this.get('modelConfig.refreshDelay');
    this.log(
      `⏲ Remote refresh queued for ${(refreshDelay / 1000).toPrecision(1)} seconds from now`,
    );
    if (isPresent(refreshDelay)) {
      yield timeout(refreshDelay);
      this.cache._removeRefreshTask(this.key);
      yield this.fetchFromRemote.perform();
    }
  }),

  _handleSetDataError(error) {
    this.log(`❌ Failed to set local data [${error.message}]`);
    if (QUOTA_EXCEEDED_EXCEPTION_MESSAGE_REGEX.test(error.message)) {
      this.errors.pushObject({ code: ERROR_CODES.QUOTA_EXCEEDED, message: error.message });
    } else {
      this.errors.pushObject({ code: ERROR_CODES.UNKNOWN_ERROR, message: error.message });
    }
  },

  _getItemSafely(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return;
    }
  },

  log(line) {
    if (this.get('modelConfig.loggingEnabled')) {
      console.debug(`MODEL DATA CACHE : [${moment().format('H:mm:ss')}] : [${this.key}] : ${line}`);
    }
  },

  _assertPresentOrThrow(key, error) {
    if (isNone(this.get(key))) {
      throw error;
    }
  },
}).reopenClass({
  clearAll(namespace) {
    if (isNone(namespace)) {
      throw new Error(`A cache namespace is required to clear the model data cache`);
    }
    Object.keys(localStorage)
      .filter((key) => key.startsWith(namespace))
      .forEach((key) => {
        localStorage.removeItem(key);
      });
  },
});
